import { useEffect, useMemo, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, Coins, Check, Loader2, Sparkles, X, CalendarDays } from "lucide-react";
import { TackShopSection } from "@/components/game/PerfTack";
import { HorseArt } from "@/components/game/HorseArt";

const SECTIONS = [{ k: "perftack", l: "Tack Shop" }, { k: "tack", l: "Cosmetics" }, { k: "barn", l: "Barn Shop" }, { k: "stall", l: "Stall Shop" }];
const RARITY = { common: "#9aa0a6", rare: "#2f6bb7", epic: "#8a3fb0" };
const isNewBg = (iso) => { if (!iso) return false; const t = Date.parse(iso); return !isNaN(t) && (Date.now() - t) < 14 * 864e5; };

export const BackgroundShopSection = ({ onUpdate, keptHorses = [] }) => {
  const [bg, setBg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewHorse, setPreviewHorse] = useState(keptHorses[0]?.id || "");
  const load = () => gameApi.get("/game/backgrounds/catalog").then((r) => setBg(r.data)).catch(() => setBg({ shop: [], owned: [], currency: 0 }));
  useEffect(() => { load(); }, []);
  const buy = async (id) => {
    setBusy(true);
    try {
      await gameApi.post("/game/backgrounds/buy", { bg_id: id });
      toast.success("Background purchased — equip it from a horse's profile.");
      load(); onUpdate && onUpdate(); setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not buy."); }
    finally { setBusy(false); }
  };
  if (!bg) return null;
  const shop = bg.shop || [];
  const ownedCount = Object.fromEntries((bg.owned || []).map((o) => [o.bg_id, o.owned]));
  const selHorse = keptHorses.find((h) => h.id === previewHorse) || keptHorses[0] || null;
  return (
    <div className="mt-3" data-testid="shop-backgrounds-section">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-secondary"><Sparkles size={13} /> Profile Backgrounds</p>
      {shop.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No backgrounds available right now — check back soon.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {shop.map((b) => (
            <div key={b.id} data-testid={`shop-bg-${b.id}`} className="rounded-2xl border border-border bg-card p-2.5">
              <button data-testid={`shop-bg-preview-${b.id}`} onClick={() => setPreview(b)}
                className="relative block h-16 w-full overflow-hidden rounded-xl bg-muted active:scale-[0.98]">
                {b.image_path ? <img src={b.image_path} alt={b.name} className="h-full w-full object-cover" /> : null}
                {isNewBg(b.created_at) && <span data-testid={`shop-bg-new-${b.id}`} className="absolute left-1 top-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">New</span>}
                <span className="absolute bottom-1 right-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[9px] font-bold text-white">Tap to preview</span>
              </button>
              <p className="mt-1.5 truncate text-xs font-bold text-primary">{b.name}</p>
              <p className="text-[11px] text-muted-foreground">{ownedCount[b.id] ? `Owned ×${ownedCount[b.id]}` : "Cosmetic background"}</p>
              <button data-testid={`shop-bg-buy-${b.id}`} disabled={busy} onClick={() => buy(b.id)}
                className="mt-1.5 flex h-8 w-full items-center justify-center gap-1 rounded-lg bg-secondary text-xs font-bold text-white active:scale-95 disabled:opacity-60">
                <Coins size={12} /> {b.price}
              </button>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="shop-bg-preview-modal" onClick={() => setPreview(null)}>
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-card p-4 shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 font-display text-lg font-bold text-primary">{preview.name}{isNewBg(preview.created_at) && <span className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">New</span>}</p>
              <button data-testid="shop-bg-preview-close" onClick={() => setPreview(null)} className="rounded-full p-1 text-muted-foreground hover:text-primary"><X size={18} /></button>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-border" data-testid="shop-bg-preview-canvas">
              <img src={preview.image_path} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="relative flex items-center justify-center px-4 py-6" style={{ minHeight: 220 }}>
                {selHorse ? <HorseArt src={selHorse.art_image} color={selHorse.color_hex} size={200} />
                  : <span className="rounded-full bg-black/45 px-3 py-1 text-xs font-bold text-white">Keep a horse to preview it here</span>}
              </div>
            </div>
            {keptHorses.length > 1 && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs font-semibold text-muted-foreground">Preview on:</span>
                <select data-testid="shop-bg-preview-horse" value={previewHorse} onChange={(e) => setPreviewHorse(e.target.value)}
                  className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-semibold text-primary">
                  {keptHorses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </div>
            )}
            <p className="mt-3 text-center text-[11px] text-muted-foreground">Cosmetic only — equip it to a horse from its profile after buying.</p>
            <button data-testid="shop-bg-preview-buy" disabled={busy} onClick={() => buy(preview.id)}
              className="mt-2 flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-secondary font-bold text-white active:scale-95 disabled:opacity-60">
              <Coins size={15} /> Buy for {preview.price}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const GameShop = ({ onBack, keptHorses = [], onUpdate, onOpenUpgrades, gameYear }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("perftack");
  const [cat, setCat] = useState("All");
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [equipHorse, setEquipHorse] = useState(keptHorses[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    gameApi.get("/game/shop/cosmetics").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const items = data?.items || [];
  const owned = data?.owned || [];
  const equipped = data?.equipped || {};
  const barnDecor = data?.stable_decor?.barn || {};
  const cats = useMemo(() => ["All", ...Array.from(new Set(items.filter((i) => i.section === section).map((i) => i.category)))], [items, section]);
  const shown = items.filter((i) => i.section === section && (cat === "All" || i.category === cat) && (!ownedOnly || owned.includes(i.id)));

  const isEquipped = (it) => {
    if (it.section === "barn") return barnDecor[it.slot] === it.id;
    if (it.section === "tack") return Object.values(equipped).some((h) => h[it.slot] === it.id);
    return false;
  };

  const doBuy = async (it, andEquip) => {
    setBusy(true); setConfirm(null);
    try {
      const { data: res } = await gameApi.post("/game/cosmetics/buy", {
        item_id: it.id, and_equip: andEquip,
        horse_id: (it.section === "tack" || it.section === "stall") ? equipHorse : null,
      });
      if (res.already_owned) toast.info("You already own that item.");
      else toast.success(`Purchased ${it.name}!`);
      if (res.player) onUpdate && onUpdate(res.player);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not purchase."); }
    finally { setBusy(false); }
  };

  const doEquip = async (it) => {
    setBusy(true);
    try {
      const { data: res } = await gameApi.post("/game/cosmetics/equip", {
        item_id: it.id, horse_id: (it.section === "tack" || it.section === "stall") ? equipHorse : null,
      });
      toast.success(`Equipped ${it.name}`); if (res.player) onUpdate && onUpdate(res.player); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not equip."); }
    finally { setBusy(false); }
  };

  const doUnequip = async (it) => {
    setBusy(true);
    try {
      const hid = it.section === "tack"
        ? Object.keys(equipped).find((h) => equipped[h][it.slot] === it.id)
        : equipHorse;
      const { data: res } = await gameApi.post("/game/cosmetics/unequip", { section: it.section, slot: it.slot, horse_id: hid });
      toast.success(`Unequipped ${it.name}`); if (res.player) onUpdate && onUpdate(res.player); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not unequip."); }
    finally { setBusy(false); }
  };

  return (
    <div className="pb-10" data-testid="shop-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="shop-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <ShoppingBag className="text-secondary" size={20} />
        <h2 className="font-display text-xl font-bold text-primary">Tack &amp; Barn Shop</h2>
        {onOpenUpgrades && <button data-testid="shop-open-upgrades" onClick={onOpenUpgrades} className="ml-auto flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-xs font-bold text-secondary active:scale-95"><Sparkles size={13} /> Upgrades</button>}
        {gameYear != null && <span data-testid="shop-game-year" className={`${onOpenUpgrades ? "" : "ml-auto"} flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-sm font-bold text-secondary`}><CalendarDays size={14} /> Year {gameYear}</span>}
        <span data-testid="shop-currency" className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-sm font-bold text-amber-700"><Coins size={14} /> {data?.currency ?? "…"}</span>
      </div>

      <div className="px-4 pt-3">
        <div className="flex gap-1 rounded-xl bg-muted p-1">
          {SECTIONS.map((s) => (
            <button key={s.k} data-testid={`shop-tab-${s.k}`} onClick={() => { setSection(s.k); setCat("All"); }}
              className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${section === s.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{s.l}</button>
          ))}
        </div>

        {section === "perftack" && (<>
          <TackShopSection keptHorses={keptHorses} onUpdate={onUpdate} />
          <BackgroundShopSection onUpdate={load} keptHorses={keptHorses} />
        </>)}
        {section !== "perftack" && (<>
        {(section === "tack" || section === "stall") && keptHorses.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Equip to:</span>
            <select data-testid="shop-horse-select" value={equipHorse} onChange={(e) => setEquipHorse(e.target.value)}
              className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-semibold text-primary">
              {keptHorses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
        )}

        <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
          {cats.map((c) => (
            <button key={c} data-testid={`shop-cat-${c}`} onClick={() => setCat(c)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${cat === c ? "bg-secondary text-white" : "bg-muted text-muted-foreground"}`}>{c}</button>
          ))}
          <button data-testid="shop-owned-toggle" onClick={() => setOwnedOnly((v) => !v)}
            className={`ml-auto shrink-0 rounded-full px-3 py-1 text-xs font-bold ${ownedOnly ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>Owned</button>
        </div>

        {loading ? (
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading shop…</p>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {shown.map((it) => {
              const own = owned.includes(it.id);
              const eq = isEquipped(it);
              return (
                <div key={it.id} data-testid={`shop-item-${it.id}`} className="rounded-2xl border border-border bg-card p-2.5">
                  <div className="relative h-16 w-full overflow-hidden rounded-xl" style={{ background: it.color }}>
                    {it.rarity && it.rarity !== "common" && (
                      <span className="absolute right-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white" style={{ background: RARITY[it.rarity] }}>{it.rarity}</span>
                    )}
                    {eq && <span className="absolute left-1 top-1 rounded-full bg-white/90 px-1.5 py-0.5 text-[9px] font-bold text-green-700">Equipped</span>}
                  </div>
                  <p className="mt-1.5 truncate text-xs font-bold text-primary">{it.name}</p>
                  <p className="text-[11px] text-muted-foreground">{it.category}</p>
                  {!own ? (
                    <button data-testid={`shop-buy-${it.id}`} disabled={busy} onClick={() => setConfirm(it)}
                      className="mt-1.5 flex h-8 w-full items-center justify-center gap-1 rounded-lg bg-secondary text-xs font-bold text-white active:scale-95">
                      <Coins size={12} /> {it.price}
                    </button>
                  ) : eq ? (
                    <button data-testid={`shop-unequip-${it.id}`} disabled={busy} onClick={() => doUnequip(it)}
                      className="mt-1.5 h-8 w-full rounded-lg border border-border text-xs font-bold text-primary active:scale-95">Unequip</button>
                  ) : (
                    <button data-testid={`shop-equip-${it.id}`} disabled={busy} onClick={() => doEquip(it)}
                      className="mt-1.5 h-8 w-full rounded-lg bg-primary text-xs font-bold text-primary-foreground active:scale-95">
                      <Check size={12} className="mr-1 inline" />Equip
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {section === "barn" && <BackgroundShopSection onUpdate={load} keptHorses={keptHorses} />}
        </>)}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="buy-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="mx-auto mb-2 h-14 w-full max-w-[160px] rounded-xl" style={{ background: confirm.color }} />
            <h3 className="font-display text-lg font-bold text-primary">{confirm.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">Purchase this item for {confirm.price} coins?</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="buy-cancel-btn" onClick={() => setConfirm(null)} className="h-11 flex-1 rounded-xl border-2 border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="buy-confirm-btn" disabled={busy} onClick={() => doBuy(confirm, false)} className="h-11 flex-1 rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">Confirm</button>
            </div>
            <button data-testid="buy-equip-btn" disabled={busy} onClick={() => doBuy(confirm, true)}
              className="mt-2 h-10 w-full rounded-xl bg-primary text-sm font-bold text-primary-foreground disabled:opacity-60 active:scale-95">Buy &amp; Equip</button>
          </div>
        </div>
      )}
    </div>
  );
};
